import { urlValidation } from 'app/modules/auth/helper'
import { FileUploadModal, reportsService } from 'app/modules/reports'
import { FilePicker } from 'app/modules/reports/components/file-picker'
import { UploadFilePayload } from 'app/modules/reports/types'
import { applicationConfiguration } from 'app/modules/shared'
import { mainNotificationContext } from 'app/modules/shared/context/main-notification-context'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { getPageUrl } from 'app/modules/shared/routing'
import pageResolver from 'app/modules/shared/routing/resolve-page'
import React, { useCallback, useContext, useState } from 'react'
import Button from 'app/modules/shared/components/button'
import { useNavigate } from 'react-router'
import TextInput from '../../../../components/text-input'
import { FileInputButton } from '../../../../components/file-input-button'
import { allowedFeaturesStorage } from 'app/modules/auth/helper/storage'
import { Feature } from 'app/modules/auth/types'
import { Trans } from 'react-i18next'
import { useResponsive } from 'app/modules/shared/hooks/use-responsive'

const ScannerModule = () => {
  const [link, setLink] = useState('')
  const [linkError, setLinkError] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | string | null>()
  const [flowId, setFlowId] = useState('')
  const { _t } = useTypedTranslation()
  const config = useContext(applicationConfiguration)
  const notificationContext = useContext(mainNotificationContext)
  const navigate = useNavigate()
  const features = allowedFeaturesStorage.getFeatures()
  const isAdvancedScanningAllowed = features && features.indexOf(Feature.Advanced_scanning) !== -1
  const { isMobile } = useResponsive([{ maxWidth: 991, isMobile: true }], { isMobile: false })

  const selectFile = useCallback(
    (files: FileList | File[]) => {
      if (selectedFile) return

      if (files[0].size > config.maxUploadSize * 1048576) {
        // Mega Bytes to bytes
        const error = _t('limit-upload-size', { maxUploadSize: config.maxUploadSize })
        notificationContext.setMessageData(error, 'danger')
      } else if (!files[0].size) {
        const error = `File should not be empty`
        notificationContext.setMessageData(error, 'danger')
      } else {
        setSelectedFile(files[0])
      }
    },
    [config.maxUploadSize, selectedFile, notificationContext, _t]
  )

  const changeLink = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim()
      setLink(value)
      setLinkError(value && urlValidation(value, _t))
    },
    [_t]
  )

  const analyzeLink = useCallback(() => {
    setSelectedFile(link)
  }, [link])

  const uploadFile = useCallback(async (data: UploadFilePayload) => {
    const id = await reportsService.uploadFiles(data, isAdvancedScanningAllowed)
    // Some timeout for UX
    setTimeout(() => {
      setFlowId(id)
    }, 1000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hideAction = (force = false, quotaExceeded = false) => {
    const result = force || window.confirm('Do you want to abort current scan?')
    if (result) {
      setSelectedFile(null)
    }

    // Navigate to error page
    if (quotaExceeded) {
      navigate(getPageUrl('quota-exceeded') as string)
    }
  }

  if (flowId.length > 0) {
    if (flowId === 'false') {
      return pageResolver.redirectTo('home')
    } else {
      return pageResolver.redirectTo('upload-initial-page', { flowId: flowId })
    }
  }

  const enableLink = !!link && !linkError

  return (
    <>
      <div className="d-flex-ds3 flex-column w-100 h-100">
        <div className="d-block">
          <p className="scanner-label">
            <Trans i18nKey="copy-paste-url">
              Copy paste your <span className="scanner-label-highligh">URL</span>
            </Trans>
          </p>

          <div className="d-flex align-items-stretch">
            <div className="flex-grow-1 me-2">
              <TextInput
                id="upload-link"
                className="align-self-center"
                placeholder={_t('file-link-placeholder')}
                value={link}
                onChange={changeLink}
                prefix={isMobile ? undefined : <i className="ds3-icon ds3-link cursor-default" />}
                // postfix={<i className="ds3 ds3-help" />} // Disabled helper until having helper text
                size={isMobile ? 'default' : 'big'}
                error={!!linkError}
              />
            </div>
            <Button id="scanner-analyze-link" variant="primary" disabled={!enableLink} onClick={analyzeLink}>
              {_t('analyze-link')}
            </Button>
          </div>
          <div className={`text-label invalid-feedback upload-link-feedback d-block ${linkError ? 'active' : ''}`}>
            {linkError ?? ''}
          </div>
        </div>

        <div className="hide-mobile d-flex flex-column flex-grow-1">
          <div className="d-flex justify-content-between">
            <div className="scanner-label">
              <Trans i18nKey="drag-and-drop">
                Drag & Drop your <span className="scanner-label-highligh text-capital">file</span>
              </Trans>
            </div>
            <div className="text-caption color-700">
              <i>{_t('max-size-hint', { maxUploadSize: config.maxUploadSize })}</i>
            </div>
          </div>

          <FilePicker onDrop={selectFile}>
            <p className="d-flex justify-content-center m-0">
              <i className="dropzone-folder-icon" />
            </p>
          </FilePicker>
        </div>
        <div className="hide-desktop">
          <p className="scanner-label">
            <Trans i18nKey="upload-your-file">
              Upload your <span className="scanner-label-highligh text-capital">file</span>
            </Trans>
          </p>
          <FileInputButton handleFile={selectFile} buttonId="file-upload" />
        </div>
      </div>
      {selectedFile && <FileUploadModal show={true} file={selectedFile} onSubmit={uploadFile} onHide={hideAction} />}
    </>
  )
}

export default ScannerModule
